// Knowledge Filer — what the nightly filer did: each night's offered items,
// bank writes with outcomes, cost, and a link to the agent-console transcript.
// Admin-only (sidebar-gated; every endpoint is require_admin). Replaces the
// Meeting Routing page, retired with the routed extraction sweep.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, StatusBadge } from '../components/common';
import { formatDateTime, formatRelativeTime } from '../utils/dateFormatters';
import {
  knowledgeFilerApi,
  type FilerRunDetail,
  type FilerRunList,
  type FilerStatus,
  type LedgerList,
} from '../services/knowledgeFiler';
import {
  bankLabel,
  consoleLink,
  formatCost,
  formatDuration,
  runOutcomeLabel,
  runStatusVariant,
  toolLabel,
  writeVariant,
  writesByBank,
} from './knowledgeFiler.helpers';
import styles from './KnowledgeFilerPage.module.css';

export function KnowledgeFilerPage() {
  const [status, setStatus] = useState<FilerStatus | null>(null);
  const [runs, setRuns] = useState<FilerRunList | null>(null);
  const [ledger, setLedger] = useState<LedgerList | null>(null);
  const [openRun, setOpenRun] = useState<FilerRunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, r, l] = await Promise.all([
        knowledgeFilerApi.status(),
        knowledgeFilerApi.runs(30),
        knowledgeFilerApi.ledger(100),
      ]);
      setStatus(s);
      setRuns(r);
      setLedger(l);
    } catch (err) {
      console.error('Failed to load knowledge filer data:', err);
      setError('Could not load the filer data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const showRun = async (id: string) => {
    if (openRun?.id === id) {
      setOpenRun(null);
      return;
    }
    try {
      setOpenRun(await knowledgeFilerApi.run(id));
    } catch (err) {
      console.error('Failed to load run:', err);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Knowledge Filer</h1>
          <p className={styles.pageSubtitle}>
            One Merlin turn a night over everything new, filing into every Knowledge Bank with
            full autonomy. Each night is audited here and as a console conversation.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void fetchData()} disabled={loading}>
          Refresh
        </Button>
      </div>

      {error && <div className={styles.formError}>{error}</div>}

      <Card>
        <CardHeader>Status</CardHeader>
        <CardBody>
          {status ? (
            <div className={styles.statusRow}>
              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Enabled here</span>
                <StatusBadge variant={status.enabled ? 'success' : 'neutral'}>
                  {status.enabled ? 'on' : 'off'}
                </StatusBadge>
              </div>
              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Schedule</span>
                <span className={styles.statusValue}>{status.schedule_utc} UTC nightly</span>
              </div>
              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Model</span>
                <span className={styles.statusValue}>{status.model}</span>
              </div>
              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Per night</span>
                <span className={styles.statusValue}>up to {status.max_items} items</span>
              </div>
              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Backfill since</span>
                <span className={styles.statusValue}>{formatDateTime(status.backfill_since)}</span>
              </div>
              {runs && (
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>Last {runs.items.length} nights</span>
                  <span className={styles.statusValue}>
                    {formatCost(runs.total_cost_usd)} · {runs.total_offered} offered · {runs.total_writes_ok} landed
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className={styles.help}>{loading ? 'Loading…' : 'No status.'}</p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>Nights</CardHeader>
        <CardBody>
          {runs && runs.items.length === 0 && <p className={styles.empty}>No runs yet.</p>}
          {runs && runs.items.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Status</th>
                    <th>Offered</th>
                    <th>Writes</th>
                    <th>Outcome</th>
                    <th>Cost</th>
                    <th>Per item</th>
                    <th>Duration</th>
                    <th>Transcript</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.items.map((run) => {
                    const link = consoleLink(run.conversation_id);
                    return (
                      <tr key={run.id} data-testid={`run-${run.id}`}>
                        <td title={formatDateTime(run.started_at)}>{formatRelativeTime(run.started_at)}</td>
                        <td>
                          <StatusBadge variant={runStatusVariant(run.status)}>{run.status}</StatusBadge>
                        </td>
                        <td>{run.offered}</td>
                        <td>
                          {run.writes_ok}/{run.writes}
                        </td>
                        <td>{runOutcomeLabel(run)}</td>
                        <td>{formatCost(run.cost_usd)}</td>
                        <td>{formatCost(run.cost_per_item)}</td>
                        <td>{formatDuration(run.duration_seconds)}</td>
                        <td className={styles.rowActions}>
                          <Button variant="secondary" size="sm" onClick={() => void showRun(run.id)}>
                            {openRun?.id === run.id ? 'Hide' : 'Writes'}
                          </Button>
                          {link && <Link to={link}>Console</Link>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {openRun && (
            <div className={styles.statusNote} data-testid="run-detail">
              <p>
                <strong>{formatDateTime(openRun.started_at)}</strong> — {openRun.offered} offered,{' '}
                {openRun.tool_calls} tool calls,{' '}
                {writesByBank(openRun.write_rows)
                  .map((b) => `${b.bank} ${b.landed}${b.failed ? ` (+${b.failed} failed)` : ''}`)
                  .join(' · ') || 'no bank writes'}
              </p>
              {openRun.write_rows.length > 0 && (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Bank</th>
                        <th>Action</th>
                        <th>Landed</th>
                        <th>From</th>
                        <th>What</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openRun.write_rows.map((w, i) => (
                        <tr key={`${w.server_id}-${i}`}>
                          <td>{bankLabel(w.server_id)}</td>
                          <td>{toolLabel(w.tool)}</td>
                          <td>
                            <StatusBadge variant={writeVariant(w)}>
                              {w.ok === true ? 'yes' : w.ok === false ? 'no' : '?'}
                            </StatusBadge>
                          </td>
                          <td className={styles.matchKey}>{w.source_id ? w.source_id.slice(0, 8) : '—'}</td>
                          <td title={w.ok === false ? w.result : undefined}>{w.headline || w.result}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {openRun.agent_text && (
                <details>
                  <summary>Agent summary</summary>
                  <pre className={styles.help}>{openRun.agent_text}</pre>
                </details>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>Done-ledger{ledger ? ` (${ledger.total})` : ''}</CardHeader>
        <CardBody>
          {ledger && ledger.items.length === 0 && <p className={styles.empty}>Nothing filed yet.</p>}
          {ledger && ledger.items.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Kind</th>
                    <th>Writes credited</th>
                    <th>Filed</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.items.map((row) => (
                    <tr key={`${row.source_kind}-${row.source_id}`}>
                      <td>
                        {row.title ?? row.source_id}{' '}
                        <span className={styles.deliveredAs}>{row.source_id.slice(0, 8)}</span>
                      </td>
                      <td>{row.source_kind}</td>
                      <td>{row.entries_written}</td>
                      <td title={formatDateTime(row.filed_at)}>{formatRelativeTime(row.filed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default KnowledgeFilerPage;
